/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useMemo} from "react";
import classes from "@/app/components/brands/BrandDetailsView/brandDetailsView.module.scss";
import {useAppSelector} from "@/app/hooks/redux";
import {TBrandFullItem} from "@/app/types/brands/brandTypes";
import Breadcrumbs from "@/app/components/breadcrumbs/Breadcrumbs";
import {Container, Typography} from "@mui/material";
import LayoutSidebar from "@/app/components/layouts/LayoutSidebar";
import {BRAND_DETAILS_PAGE_PREFIX, BRANDS_LIST_PAGE_SLUG, brandsConfig} from "@/app/config/brandsConfig";
import productsConfig from "@/app/config/products.config";
import jsonToJsx from "@/app/utils/jsonToJsx";
import parse from "html-react-parser";
import Image from "next/image";
import AlgoliaProvider from "@/app/components/algolia/AlgoliaProvider";
import AlgoliaSidebar from "@/app/components/algolia/AlgoliaSidebar";
import ToolbarTop from "@/app/components/algolia/ToolbarTop";
import AlgoliaHits from "@/app/components/algolia/hits/AlgoliaHits";
import ToolbarBottom from "@/app/components/algolia/ToolbarBottom";
import {transformAlgoliaFacetsSettings} from "@/app/utils/facetsConfigTransform";
import SearchBox from "@/app/components/algolia/SearchBox";
import {useTranslation} from "next-i18next";
import {Configure} from "react-instantsearch";

const BrandDetailsView = (props: {
    brand: TBrandFullItem,
    className?: string
}) => {
    const {t} = useTranslation();
    const {storeConfig:storeConfigData} = useAppSelector(state => state.storeConfigReducer);
    const brand = props.brand;
    const brandName = brand.brand_name ?? '';
    const urlPath = `/${BRAND_DETAILS_PAGE_PREFIX}/${brand.url_slug}`;
    const topBanner = brand.top_banner_cms_block?.content_json ? jsonToJsx(brand.top_banner_cms_block?.content_json) : undefined;

    const facetsConfig = transformAlgoliaFacetsSettings({
        algoliaFacetsString: storeConfigData?.algolia_instant_search_facets,
        facetsNumber: storeConfigData?.algolia_instant_search_number_facets,
    });

    const algoliaProviderConfig = {
        customConfig: {
            filters: `brand:"${brandName}"`,
        }
    }

    const regularFacetsConfig = useMemo(() => facetsConfig.filter(f => f.type !== "disjunctive").map(f => f.attribute), [facetsConfig]);
    const menuFacetsConfig = useMemo(() => facetsConfig.filter(f => f.type === "disjunctive").map(f => ({
        name: f.attribute,
        attributes: [f.attribute],
    })), [facetsConfig]);

    return <LayoutSidebar
        headTitle={brandName}
        metaDescription={brand.meta_description ?? brandName}
        canonical={urlPath}
        sidebarPosition={'left'}
        mainContainerClassName={'category-page-content'}
        contentWrapperComponent={AlgoliaProvider}
        contentWrapperComponentProps={{config: algoliaProviderConfig}}
        sidebarContent={<>
            <Configure disjunctiveFacets={regularFacetsConfig} hierarchicalFacets={menuFacetsConfig} />
            <AlgoliaSidebar facetsConfig={facetsConfig}/>
        </>}
        beforeContent={
            <>
                {topBanner && <div className='top-banner-wrapper do-not-print'>{topBanner}</div>}

                <Breadcrumbs additionalBreadcrumbs={[
                    {
                        url: `/${BRANDS_LIST_PAGE_SLUG}`,
                        title: storeConfigData?.brand_listing_page_title ?? brandsConfig.defaultValues.brandsListTitle,
                    }
                ]} currentPageTitle={brandName}/>

                <Container>
                    <Typography variant="h1" component="h1" className={classes.title}>{brandName}</Typography>
                </Container>
            </>
        }>
        <div className={classes.brandInfo}>
            <div className={classes.logoWrapper}>
                <div className={classes.logo}>
                    {brand.logo ? <Image
                        src={brand.logo}
                        fill={true}
                        alt={brand.logo_alt ?? brand.brand_name ?? ''}
                    /> : <span className={classes.noLogo}>{t("No logo")}</span>}
                </div>
            </div>
            {brand.description && <div className={classes.desc}>{parse(brand.description ?? '')}</div>}
        </div>
        <div className={`products-wrapper`}>
            {storeConfigData?.algolia_instant_search_search_box_enabled === "1" &&
                <SearchBox defaultText='' placeholder={t('Search for products')}/>}

            <ToolbarTop facetsConfig={facetsConfig}/>

            <AlgoliaHits preloaderProductsCount={productsConfig.productsPerPageDefault}/>

            <ToolbarBottom/>
        </div>
    </LayoutSidebar>
}

export default BrandDetailsView;